import { ArrowUpRight, Users, UserPlus, TrendingUp, LogOut, BarChart3, Brain, Boxes, Cpu, Database } from "lucide-react";

const FUNCS = ["Sales", "Marketing", "Finance & Accounting", "Supply Chain", "Customer Service", "HR & People Ops"];
const AGENTS = [
  { icon: Users, t: "Talent Acquisition" },
  { icon: UserPlus, t: "Employee Onboarding" },
  { icon: TrendingUp, t: "Performance & Talent" },
  { icon: LogOut, t: "Employee Offboarding" },
];
const SOLS = [
  { name: "AI Over BI", icon: BarChart3 },
  { name: "Enterprise Brain", icon: Brain },
  { name: "Data Products", icon: Boxes },
  { name: "AI Models", icon: Cpu },
  { name: "Dashboards & Reporting", icon: Database },
];

/* ============ SECTION 4 · Business Functions — DARK, vertical left-rail ============ */
const SectionFunctions = () => (
  <section className="bg-[#211307] py-14 lg:py-16 rounded-2xl">
    <div className="px-6 lg:px-10">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-[#E8AA68]">Business Function Solutions</p>
      <h2 className="font-display text-2xl lg:text-[2.5rem] font-medium tracking-tight text-[#F7F5F0] mt-3 leading-[1.12]">Transform Business Processes into Agentic Applications</h2>
      <p className="text-[13px] md:text-sm text-white/60 mt-3 max-w-2xl leading-relaxed">Bring intelligence, autonomy and action into the core functions of your business — from Sales and Marketing to Finance, Supply Chain, Customer Service and HR.</p>

      <div className="grid lg:grid-cols-[0.3fr_1fr] gap-6 lg:gap-10 mt-8">
        {/* Vertical left rail */}
        <div className="flex flex-col gap-1">
          {FUNCS.map((n, i) => (
            <button key={n} className={`text-left rounded-lg px-4 py-3 text-[13.5px] font-semibold transition-colors flex items-center justify-between group ${i === 5 ? "bg-white/10 text-[#F7F5F0] border-l-2 border-[#E8AA68]" : "text-white/50 hover:text-white/80"}`}>
              {n}
              <ArrowUpRight size={15} className={`${i === 5 ? "text-[#E8AA68] opacity-100" : "opacity-0 group-hover:opacity-60"} transition-opacity`} />
            </button>
          ))}
        </div>

        {/* Detail */}
        <div>
          <h3 className="font-display text-xl lg:text-2xl font-medium text-[#F7F5F0] tracking-tight">Reimagine the Employee Lifecycle with Agentic AI</h3>
          <p className="text-[13px] text-white/60 mt-2 leading-relaxed max-w-2xl">Transform how HR teams hire, onboard, support, develop and manage employees with intelligent AI agents.</p>

          <div className="grid lg:grid-cols-[1fr_0.5fr] gap-5 mt-5 items-stretch">
            <div>
              {/* use-case block */}
              <div className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
                <h4 className="font-display text-base font-medium text-[#F7F5F0]">Hire-to-Retire</h4>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11.5px] font-semibold text-white/90">
                  {["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"].map((s, i) => (
                    <span key={s}><span className="text-[#E8AA68] font-mono2 text-[9px] mr-1">{String(i + 1).padStart(2, "0")}</span>{s}</span>
                  ))}
                </div>
              </div>
              {/* agents */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                {AGENTS.map((a) => (
                  <div key={a.t} className="rounded-lg bg-white/[0.05] border border-white/10 p-3">
                    <div className="w-7 h-7 rounded-md bg-[#E8AA68]/15 text-[#E8AA68] flex items-center justify-center mb-2"><a.icon size={13} strokeWidth={1.9} /></div>
                    <p className="text-[11.5px] font-semibold text-[#F7F5F0] leading-snug">{a.t}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* thumbnail */}
            <div className="rounded-lg overflow-hidden border border-white/10 flex flex-col">
              <div className="flex items-center gap-1.5 px-2.5 py-2 bg-white/10">
                <span className="w-2 h-2 rounded-full bg-[#E88B6B]" /><span className="w-2 h-2 rounded-full bg-[#E8C15E]" /><span className="w-2 h-2 rounded-full bg-[#9CB88A]" />
              </div>
              <div className="relative flex-1 min-h-[150px]">
                <img src="/assets/fin-reco-demo.png" alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 flex items-end justify-between">
                  <p className="text-[12px] font-semibold text-[#3E2818]">Explore the live demo</p>
                  <span className="w-7 h-7 rounded-full bg-[#211307] text-white flex items-center justify-center"><ArrowUpRight size={14} /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============ SECTION 5 · Data & Intelligence — LIGHT, horizontal tabs ============ */
const SectionData = () => (
  <section className="bg-[#F7F5F0] py-14 lg:py-16 rounded-2xl border border-[#3E2818]/10">
    <div className="px-6 lg:px-10">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-[#8A5730]">Data &amp; Intelligence Solutions</p>
      <h2 className="font-display text-2xl lg:text-[2.5rem] font-medium tracking-tight text-[#3E2818] mt-3 leading-[1.12]">Turn Enterprise Data into Decision Intelligence</h2>
      <p className="text-[13px] md:text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Build trusted context, intelligent data products, AI models and decision experiences that power agents, applications and business operations across the enterprise.</p>

      <div className="flex flex-wrap gap-x-7 gap-y-2 border-b border-[#3E2818]/12 mt-7 mb-6">
        {SOLS.map((s, i) => (
          <span key={s.name} className={`relative pb-3 text-[14px] font-semibold flex items-center gap-2 ${i === 0 ? "text-[#3E2818]" : "text-[#3E2818]/45"}`}>
            <s.icon size={15} className={i === 0 ? "text-[#8A5730]" : "text-[#3E2818]/40"} />{s.name}
            {i === 0 && <span className="absolute left-0 -bottom-px h-[2px] w-full bg-[#8A5730]" />}
          </span>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-start">
        <div>
          <h3 className="font-display text-xl lg:text-2xl font-medium text-[#3E2818] tracking-tight">AI Over BI</h3>
          <p className="text-[13px] text-[#3E2818]/65 mt-2 leading-relaxed max-w-2xl">Ask questions in natural language and get governed, explainable answers directly from your enterprise data — no dashboards required.</p>
          <div className="grid grid-cols-2 gap-3 mt-5">
            {["Semantic Query", "Governed Answers", "Auto Insights", "Narrative Reports"].map((t) => (
              <div key={t} className="rounded-xl bg-white border border-[#3E2818]/10 p-4">
                <p className="text-[12.5px] font-semibold text-[#3E2818]">{t}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-dashed border-[#8A5730]/35 bg-white/60 min-h-[180px] flex items-center justify-center">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A5730]">Solution visual</span>
        </div>
      </div>
    </div>
  </section>
);

export default function SectionLayoutSwitched() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Your Combination · Preview</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Functions = dark left-rail · Data = light horizontal tabs</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Section 4 (Business Functions) becomes a dark section with a vertical left-rail selector; Section 5 (Data &amp; Intelligence) becomes a light section with horizontal tabs. Confirm and I&rsquo;ll build both live.</p>

        <div className="mt-10 space-y-8">
          <SectionFunctions />
          <SectionData />
        </div>
      </div>
    </div>
  );
}
